// Sky - Background sky component with clouds, sun/moon, stars, and weather
import { memo, useState, useEffect, useRef } from 'react';

const Sky = memo(({ timeOfDay, playerX = 0, weather = 'clear' }) => {
  const gradients = {
    day: 'from-sky-300 via-sky-200 to-emerald-100',
    dawn: 'from-rose-300 via-orange-200 to-yellow-100',
    dusk: 'from-purple-400 via-rose-300 to-orange-200',
    night: 'from-indigo-900 via-slate-800 to-indigo-950'
  };
  
  const cloudOffset = playerX * 0.15;
  const isNight = timeOfDay === 'night';
  const isDusk = timeOfDay === 'dusk';
  const cloudOpacity = isNight ? 0.3 : isDusk ? 0.6 : weather === 'rain' ? 0.95 : 0.85;
  
  // Generate stable star positions
  const starsRef = useRef(null);
  if (!starsRef.current) {
    starsRef.current = Array.from({ length: 40 }, (_, i) => ({
      x: (i * 37 + 13) % 100,
      y: (i * 23 + 7) % 45,
      size: 1 + (i % 3),
      delay: (i * 0.15) % 3,
      twinkle: i % 4 === 0,
    }));
  }
  
  return (
    <div className={`absolute inset-0 bg-gradient-to-b ${gradients[timeOfDay]} transition-all duration-[3000ms] pointer-events-none`}>
      {/* Sun */}
      {timeOfDay === 'day' && (
        <div 
          className="absolute top-[6%] w-24 h-24 rounded-full transition-transform duration-200"
          style={{ 
            right: `calc(12% + ${cloudOffset * 0.2}px)`,
            background: 'radial-gradient(circle, #FFE566 0%, #FFD93D 50%, #FFA500 100%)',
            boxShadow: '0 0 60px 20px rgba(255,217,61,0.4), 0 0 100px 40px rgba(255,217,61,0.2)'
          }}
        />
      )}
      
      {/* Dawn/Dusk sun */}
      {(timeOfDay === 'dawn' || timeOfDay === 'dusk') && (
        <div 
          className="absolute w-20 h-20 rounded-full transition-all duration-[3000ms]"
          style={{ 
            top: timeOfDay === 'dawn' ? '25%' : '30%',
            right: `calc(15% + ${cloudOffset * 0.2}px)`,
            background: timeOfDay === 'dawn' 
              ? 'radial-gradient(circle, #FFE566 0%, #FF8C42 60%, #FF6B6B 100%)'
              : 'radial-gradient(circle, #FF8C42 0%, #FF6B6B 50%, #C73866 100%)',
            boxShadow: `0 0 40px 15px rgba(255,107,107,0.3)`,
            opacity: 0.9,
          }}
        />
      )}
      
      {/* Moon */}
      {isNight && (
        <div 
          className="absolute top-[10%] w-16 h-16 rounded-full transition-transform duration-200"
          style={{ 
            right: `calc(18% + ${cloudOffset * 0.2}px)`,
            background: 'radial-gradient(circle at 35% 35%, #f5f5f5 0%, #e8e8e8 50%, #d0d0d0 100%)',
            boxShadow: '0 0 30px 10px rgba(255,255,255,0.3), 0 0 60px 20px rgba(200,200,255,0.15)'
          }}
        >
          {/* Moon craters */}
          <div className="absolute w-3 h-3 rounded-full bg-gray-300/40" style={{ top: '20%', left: '25%' }} />
          <div className="absolute w-2 h-2 rounded-full bg-gray-300/30" style={{ top: '50%', left: '55%' }} />
          <div className="absolute w-1.5 h-1.5 rounded-full bg-gray-300/35" style={{ top: '35%', left: '60%' }} />
        </div>
      )}
      
      {/* Stars (visible at night and dusk) */}
      {(isNight || isDusk) && (
        <div className={`absolute inset-0 transition-opacity duration-[3000ms] ${isNight ? 'opacity-100' : 'opacity-30'}`}>
          {starsRef.current.map((star, i) => (
            <div
              key={i}
              className={star.twinkle ? 'animate-pulse' : ''}
              style={{
                position: 'absolute',
                left: `${star.x}%`,
                top: `${star.y}%`,
                width: star.size,
                height: star.size,
                backgroundColor: 'white',
                borderRadius: '50%',
                opacity: isNight ? 0.8 : 0.4,
                animationDelay: `${star.delay}s`,
                animationDuration: '2s',
              }}
            />
          ))}
        </div>
      )}
      
      {/* Clouds - affected by weather */}
      <svg className="absolute top-[8%] w-32 h-20 transition-all duration-150" style={{ left: `calc(8% - ${cloudOffset}px)`, opacity: cloudOpacity }} viewBox="0 0 100 60">
        <ellipse cx="30" cy="35" rx="25" ry="18" fill={weather === 'rain' ? '#94a3b8' : 'white'} />
        <ellipse cx="55" cy="30" rx="30" ry="22" fill={weather === 'rain' ? '#94a3b8' : 'white'} />
        <ellipse cx="75" cy="38" rx="20" ry="15" fill={weather === 'rain' ? '#94a3b8' : 'white'} />
        <ellipse cx="45" cy="42" rx="22" ry="14" fill={weather === 'rain' ? '#64748b' : '#f0f0f0'} />
      </svg>
      
      <svg className="absolute top-[12%] w-40 h-24 transition-all duration-150" style={{ left: `calc(35% - ${cloudOffset * 0.7}px)`, opacity: cloudOpacity - 0.05 }} viewBox="0 0 120 70">
        <ellipse cx="35" cy="42" rx="28" ry="20" fill={weather === 'rain' ? '#94a3b8' : 'white'} />
        <ellipse cx="65" cy="35" rx="35" ry="25" fill={weather === 'rain' ? '#94a3b8' : 'white'} />
        <ellipse cx="95" cy="45" rx="22" ry="16" fill={weather === 'rain' ? '#94a3b8' : 'white'} />
        <ellipse cx="55" cy="50" rx="25" ry="15" fill={weather === 'rain' ? '#64748b' : '#f0f0f0'} />
      </svg>
      
      <svg className="absolute top-[5%] w-28 h-18 transition-all duration-150" style={{ right: `calc(25% + ${cloudOffset * 0.5}px)`, opacity: cloudOpacity }} viewBox="0 0 90 55">
        <ellipse cx="25" cy="32" rx="22" ry="16" fill={weather === 'rain' ? '#94a3b8' : 'white'} />
        <ellipse cx="50" cy="28" rx="28" ry="20" fill={weather === 'rain' ? '#94a3b8' : 'white'} />
        <ellipse cx="70" cy="35" rx="18" ry="14" fill={weather === 'rain' ? '#94a3b8' : 'white'} />
      </svg>
      
      <svg className="absolute top-[15%] w-36 h-22 transition-all duration-150" style={{ right: `calc(5% + ${cloudOffset * 0.6}px)`, opacity: cloudOpacity - 0.15 }} viewBox="0 0 110 65">
        <ellipse cx="30" cy="38" rx="26" ry="18" fill={weather === 'rain' ? '#94a3b8' : 'white'} />
        <ellipse cx="60" cy="32" rx="32" ry="22" fill={weather === 'rain' ? '#94a3b8' : 'white'} />
        <ellipse cx="88" cy="40" rx="20" ry="15" fill={weather === 'rain' ? '#94a3b8' : 'white'} />
      </svg>
      
      {/* Rain effect overlay */}
      {weather === 'rain' && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ opacity: isNight ? 0.4 : 0.6 }}>
          <style>{`
            @keyframes rain-fall {
              0% { transform: translateY(-100%) translateX(0); opacity: 0; }
              10% { opacity: 1; }
              90% { opacity: 1; }
              100% { transform: translateY(100vh) translateX(-20px); opacity: 0; }
            }
          `}</style>
          {Array.from({ length: 30 }, (_, i) => (
            <div
              key={`rain-${i}`}
              style={{
                position: 'absolute',
                left: `${(i * 3.4) % 100}%`,
                top: `-${(i * 7) % 20}%`,
                width: 1.5,
                height: 12 + (i % 8),
                background: 'linear-gradient(to bottom, transparent, rgba(150,180,220,0.6))',
                borderRadius: 2,
                animation: `rain-fall ${0.8 + (i % 5) * 0.15}s linear infinite`,
                animationDelay: `${(i * 0.07) % 1.2}s`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
});

export default Sky;
